import { arrowDownIcon, arrowUpIcon, triangleDownIcon } from '../../utils/images';
import { DraggableItem, ItemType } from '../../types/Scenario';
import { useSensors } from '../../contexts/SensorContext';
import './ScenarioCard.scss';
import { useState } from 'react';

// Import dummy data
import productsData from '../../data/products.json';
import imagesData from '../../data/images.json';
import displaysData from '../../data/displays.json';

// Define the list of standalone actions
const actions: string[] = [
 "pick up", "wave hand", "display"
];

// Define the sensors with their options and default values
interface SensorConfig {
  name: string;
  options: string[];
  defaultValue: string;
  isTextInput?: boolean;
}



// Define the customized options - only Product and Image now
const customized = {
  products: productsData.map(product => ({ id: product.id, name: product.name })),
  images: imagesData.map(image => ({ id: image.id, name: image.name, url: image.url }))
};

// Define the list of formula operators/symbols
const formulaOperators: string[] = ["?", ":", "&", "==", "or", "and"];

interface ScenarioCardProps {
  onDragStart: (e: React.DragEvent, item: DraggableItem, type: ItemType) => void;
  onDragEnd: () => void;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({ onDragStart, onDragEnd }) => {
  const { sensors } = useSensors();
  
  // Get unique sensor types from real sensor data
  const getUniqueSensorTypes = () => {
    const uniqueTypes = Array.from(new Set(sensors.map(sensor => sensor.sensor_type)));
    return uniqueTypes.map(type => ({
      name: type,
      options: sensors
        .filter(sensor => sensor.sensor_type === type)
        .map(sensor => sensor.id),
      defaultValue: type.toLowerCase().includes('mmwave') ? 'near' : '',
      isTextInput: ['rfid', 'voice'].some(keyword => type.toLowerCase().includes(keyword))
    }));
  };
  
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    display: true,
    action: true,
    sensor: true,
    customized: true,
    formula: true
  });

  // Toggle section visibility
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="scenario-card">
      <div 
        className="scenario-card__header" 
        onClick={() => toggleSection('display')}
      >
        <div className="scenario-title">Display</div>
        <img 
          className="scenario-card__icon" 
          src={expandedSections.display ? arrowUpIcon : arrowDownIcon} 
          alt={expandedSections.display ? 'Arrow up' : 'Arrow down'} 
        />
      </div>
      <div className={`scenario-card__chips ${expandedSections.display ? 'expanded' : 'collapsed'}`}>
        <div 
          className="scenario-card__chip scenario-card__chip--selected"
          draggable
          onDragStart={(e) => onDragStart(e, {
            name: "Display",
            options: displaysData.map(d => d.id)
          }, "display")}
          onDragEnd={onDragEnd}
        >
          <p>Display</p>
          <img src={triangleDownIcon} alt="Arrow down" />
        </div>
      </div>

      <div 
        className="scenario-card__header"
        onClick={() => toggleSection('action')}
      >
        <div className="scenario-title">Action</div>
        <img 
          className="scenario-card__icon" 
          src={expandedSections.action ? arrowUpIcon : arrowDownIcon} 
          alt={expandedSections.action ? 'Arrow up' : 'Arrow down'} 
        />
      </div>
      <div className={`scenario-card__actions ${expandedSections.action ? 'expanded' : 'collapsed'}`}>
        {/* Map over the actions array to render each action */}
        {actions.map((action, index) => (
          <div 
            key={index} 
            className="scenario-card__action scenario-card__action--colored"
            draggable
            onDragStart={(e) => onDragStart(e, {
              name: action,
              options: []
            }, "action")}
            onDragEnd={onDragEnd}
          >
            {action}
          </div>
        ))}
      </div>

      <div 
        className="scenario-card__header"
        onClick={() => toggleSection('sensor')}
      >
        <div className="scenario-title">Sensor</div>
        <img 
          className="scenario-card__icon" 
          src={expandedSections.sensor ? arrowUpIcon : arrowDownIcon} 
          alt={expandedSections.sensor ? 'Arrow up' : 'Arrow down'}
        />
      </div>
      <div className={`scenario-card__chips ${expandedSections.sensor ? 'expanded' : 'collapsed'}`}>
        {/* Map over the unique sensor types to render each chip */}
        {getUniqueSensorTypes().map((sensorType, index) => (
          <div 
            key={index} 
            className="scenario-card__chip"
            draggable
            onDragStart={(e) => onDragStart(e, {
              name: sensorType.name,
              options: sensorType.options,
              isTextInput: sensorType.isTextInput,
              defaultValue: sensorType.defaultValue
            }, "sensor")}
            onDragEnd={onDragEnd}
          >
            <p>{sensorType.name}</p>
            <img src={triangleDownIcon} alt="Arrow down" />
          </div>
        ))}
      </div>

      <div 
        className="scenario-card__header"
        onClick={() => toggleSection('customized')}
      >
        <div className="scenario-title">Customized</div>
        <img 
          className="scenario-card__icon" 
          src={expandedSections.customized ? arrowUpIcon : arrowDownIcon} 
          alt={expandedSections.customized ? 'Arrow up' : 'Arrow down'}
        />
      </div>
      <div className={`scenario-card__sections ${expandedSections.customized ? 'expanded' : 'collapsed'}`}>
        <div className="scenario-card__sub-title">Products</div>
          <div 
          className="scenario-card__custom scenario-card__custom--product"
            draggable
            onDragStart={(e) => onDragStart(e, {
            name: "Product",
            options: customized.products.map(p => p.id)
          }, "product")}
            onDragEnd={onDragEnd}
          >
          <p>Product</p>
            <img src={arrowDownIcon} alt="Arrow down" />
          </div>

        <div className="scenario-card__sub-title">Images</div>
          <div 
          className="scenario-card__custom scenario-card__custom--image"
            draggable
            onDragStart={(e) => onDragStart(e, {
            name: "Image", 
            options: customized.images.map(i => i.id)
          }, "image")}
            onDragEnd={onDragEnd}
          >
          <p>Image</p>
          <img src={arrowDownIcon} alt="Arrow down" />
          </div>
      </div>

      <div 
        className="scenario-card__header"
        onClick={() => toggleSection('formula')}
      >
        <div className="scenario-title">Formula</div>
        <img 
          className="scenario-card__icon" 
          src={expandedSections.formula ? arrowUpIcon : arrowDownIcon} 
          alt={expandedSections.formula ? 'Arrow up' : 'Arrow down'}
        />
      </div>
      <div className={`scenario-card__formula-buttons ${expandedSections.formula ? 'expanded' : 'collapsed'}`}>
        {/* Map over the formulaOperators array to render each button */}
        {formulaOperators.map((operator, index) => (
          <div 
            key={index} 
            className="scenario-card__formula-button"
            draggable
            onDragStart={(e) => onDragStart(e, {
              name: operator,
              options: []
            }, "formula")}
            onDragEnd={onDragEnd}
          >
            <p>{operator}</p>
          </div>
        ))}
      </div>
    </div>
  );
};